import React, { useContext, useState, useEffect } from 'react';
import Measure from 'react-measure';
import classNames from 'classnames/bind';
import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'components/Button/Button';
import Logo from 'components/Logo/Logo';
import TextBubbleWithUserProfile from 'components/TextBubbleWithUserProfile/TextBubbleWithUserProfile';
import Space, { SPACE_TYPES } from 'corecomponents/Space/Space';
import { last } from 'es-toolkit/compat';
import { Col, Grid, Row } from 'forkedlibraries/react-bootstrap';
import { useModal } from 'hooks/useModal';
import { ImprovementImportanceModal } from 'pages/PostInspectionFlow/ImprovementImportanceModal/ImprovementImportanceModal';
import { THEME_CONSTS } from 'themes/themes';
import PostInspectionFlowProgressBar from '../PostInspectionFlowProgressBar/PostInspectionFlowProgressBar';
import styles from './PostInspectionContentLayout.module.css';

// REPLACE WITH CONTENT LAYOUT!
const cx = classNames.bind(styles);

/**
 * @typedef {{
 *   ctaProps: any,
 *   setCtaProps: (ctaProps: any) => void,
 * }} PostInspectionContentLayoutContextValue
 */

const PostInspectionContentLayoutContext = React.createContext(
  /** @type {PostInspectionContentLayoutContextValue} */ ({}),
);

/**
 * `children = null` gives `children` the type `null`, so every caller passing
 * real content was told the tag accepts a single child of type `null |
 * undefined`. The same goes for the other defaulted props: a default of `[]`,
 * `true` or `0` narrows the prop to that one type rather than making it
 * optional. Declaring them here is what restores the optionality the defaults
 * were meant to express.
 *
 * @param {{
 *   actions?: import('react').ReactNode[],
 *   children?: import('react').ReactNode,
 *   employee?: any,
 *   hasCustomFixedPageHeader?: boolean,
 *   headerBorder?: boolean,
 *   isLogoVisible?: boolean,
 *   noMinHeight?: boolean,
 *   noOverlay?: boolean,
 *   showCityBackground?: boolean,
 *   stepConfig?: any,
 *   stepConfigs?: any,
 *   title?: import('react').ReactNode,
 *   topFiller?: import('react').ReactNode,
 * }} props
 */
const PostInspectionContentLayout = ({
  title,
  children = null,
  showCityBackground,
  employee,
  actions = [],
  headerBorder = true,
  topFiller = null,
  stepConfigs,
  stepConfig,
  noMinHeight = false,
  noOverlay = false,
  hasCustomFixedPageHeader = false,
  isLogoVisible = true,
}) => {
  const [ctaProps, setCtaProps] = useState(null);
  const [isModalOpen, setModalClose] = useModal(false);

  return (
    <PostInspectionContentLayoutContext.Provider
      value={{
        setCtaProps,
        ctaProps,
      }}
    >
      {ctaProps && (
        <PostInspectionContentLayout.FixedPageHeader
          headerBorder={headerBorder}
          isLogoVisible={isLogoVisible}
          actions={[
            ...actions,
            <div key={0} className={cx('header-cta-container')}>
              <Button
                disabled={ctaProps.disabled}
                buttonType={BUTTON_TYPES.DEFAULT}
                size={BUTTON_SIZES.FIT}
                onClick={ctaProps.onClick}
              >
                {ctaProps.children}
              </Button>
            </div>,
          ]}
        />
      )}
      <Grid>
        <Row className={cx('flex-row', { hasCustomFixedPageHeader })}>
          <Col
            lg={2}
            md={1}
            smHidden
            xsHidden
            className={cx('col-z-index')}
            style={{ minHeight: 0 }}
          >
            {!noOverlay && <div className={cx('overlay', 'left')} />}
          </Col>
          <Col className={cx('layout', { noMinHeight })} lg={8} md={10} xs={12}>
            {topFiller && topFiller}
            <PostInspectionFlowProgressBar
              stepConfigs={stepConfigs}
              stepConfig={stepConfig}
            />
            {title && (
              <div className={cx('title')}>
                <TextBubbleWithUserProfile employee={employee} text={title} />
              </div>
            )}
            {children}
            {!noMinHeight && <Space value={SPACE_TYPES.XXXXXL} />}
          </Col>
          <Col lg={2} md={1} smHidden xsHidden className={cx('col-z-index')}>
            {!noOverlay && <div className={cx('overlay', 'right')} />}
          </Col>
        </Row>
      </Grid>
      {showCityBackground && (
        <div className={cx('fixed-background')}>
          <img src="/city_images/city_side_fold_full.svg" alt="city" />
        </div>
      )}
      {isModalOpen && (
        <ImprovementImportanceModal show={isModalOpen} onHide={setModalClose} />
      )}
    </PostInspectionContentLayoutContext.Provider>
  );
};

/**
 * The rest element forwards anything else straight to `Button`, so the index
 * signature is deliberate. It does mean the five named props below have to be
 * declared by hand — a rest element makes React's own prop transforms bail out,
 * so neither `defaultProps` nor these defaults would otherwise reach a caller.
 *
 * @param {{
 *   children?: import('react').ReactNode,
 *   disabled?: boolean,
 *   displayfixed?: boolean,
 *   extraSpace?: number,
 *   onClick?: (event: import('react').SyntheticEvent) => void,
 *   [key: string]: any,
 * }} props
 */
PostInspectionContentLayout.CtaButton = ({
  children,
  onClick,
  disabled = false,
  extraSpace = 0,
  displayfixed = false,
  ...rest
}) => {
  const { setCtaProps } = useContext(PostInspectionContentLayoutContext);
  const [buttonBottom, setButtonBottom] = useState(null);

  useEffect(() => {
    if (buttonBottom) {
      setCtaProps({
        disabled,
        children,
        onClick,
        buttonBottom: buttonBottom + extraSpace,
        displayfixed,
      });
    }
    // Don't like checking on all this deps. But only way to get updated values inside the onClick.
  }, [
    children,
    disabled,
    buttonBottom,
    onClick,
    displayfixed,
    extraSpace,
    setCtaProps,
  ]);

  return (
    <Measure
      bounds
      onResize={({ bounds }) => {
        // bounds.bottom is relative to scrollY, we want relative to scrollY = 0
        setButtonBottom(window.scrollY + bounds.bottom);
      }}
    >
      {({ measureRef }) => (
        <div ref={measureRef} className={cx('cta-container')}>
          <Button
            buttonType={BUTTON_TYPES.DEFAULT}
            size={BUTTON_SIZES.FLUID_LONG_RESPONSIVE}
            onClick={onClick}
            disabled={disabled}
            {...rest}
          >
            {children}
          </Button>
        </div>
      )}
    </Measure>
  );
};
const fixedHeaderHeight = 60;

/**
 * @param {{
 *   actions: import('react').ReactNode[],
 *   headerBorder?: boolean,
 *   isLogoVisible?: boolean,
 * }} props
 */
PostInspectionContentLayout.FixedPageHeader = ({
  actions,
  headerBorder = true,
  isLogoVisible = true,
}) => {
  const contentLayoutContext = useContext(PostInspectionContentLayoutContext);
  const animationBoundary = contentLayoutContext?.ctaProps?.buttonBottom;
  const displayfixed = contentLayoutContext?.ctaProps?.displayfixed;
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const handleScroll = () => {
    // Only change visibility based on scroll for desktop
    if (!isMobile && !displayfixed) {
      setVisible(window.scrollY > animationBoundary - fixedHeaderHeight);
    }
  };

  const handleResize = () => {
    const newIsMobile = window.innerWidth < 768;
    setIsMobile(newIsMobile);

    // Update visibility based on new screen size
    if (newIsMobile) {
      setVisible(!!displayfixed);
    } else {
      setVisible(
        displayfixed
          ? false
          : window.scrollY > animationBoundary - fixedHeaderHeight,
      );
    }
  };

  useEffect(() => {
    // Mobile with displayfixed = true should show the header
    // Desktop with displayfixed = true should NOT show the header (ignore this flag on desktop)
    if (isMobile && displayfixed) {
      setVisible(true);
    } else if (!isMobile) {
      // On desktop, only show based on scroll position
      setVisible(window.scrollY > animationBoundary - fixedHeaderHeight);
    }

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [animationBoundary, displayfixed, isMobile]);

  return (
    <>
      <div
        role="dialog"
        aria-label="Information Navigation"
        className={`post-inspection-fixed-header ${cx('header-background', {
          hide: !visible,
          show: !!visible,
          hideBorder: !headerBorder,
        })}`}
      />
      <div
        className={`post-inspection-fixed-cta ${cx('fixed', {
          hide: !visible,
        })}`}
      >
        <Grid>
          <div className={cx('fixed-header-wrapper')}>
            <div className={cx('logo-wrappers')}>
              {isLogoVisible && <Logo theme={THEME_CONSTS.DARK} />}
            </div>
            <div className={cx('desktop-actions-wrapper')}>
              {actions.map((action, index) => (
                <div className={cx('action')} key={index}>
                  {action}
                </div>
              ))}
            </div>
            {/* Show only the last action, since there is no generic way to handle multiple
            actions for mobile */}
            <div className={cx('mobile-actions-wrapper')}>{last(actions)}</div>
          </div>
        </Grid>
      </div>
    </>
  );
};

export default PostInspectionContentLayout;
